import { LogoWithoutText } from '@c/icons/logoWithoutText';
import Avatar from '@ui/Avatar';
import { useUser } from '@util/hooks/useUser';
import type { ChatMessageDocument } from '@models/chat';
import type { UserDocument } from '@models/user';
import Link from 'next/link';
import MessageContent from './MessageContent';
import MessageImage from './MessageImage';
import MessageProduct from './MessageProduct';
import MessageTimestamp from './MessageTimestamp';
import { cn } from '@util/index';

interface MessageInboundProps {
  message: ChatMessageDocument;
  timestamp: number;
  isSupport?: boolean;
  onImageClick: (image: string) => void;
  user?: UserDocument | null;
  fallbackImg?: string;
  onRightClick?: (ev: React.MouseEvent) => void;
  isSidebar?: boolean;
}

export default function MessageInbound({
  message,
  timestamp,
  isSupport,
  onImageClick,
  fallbackImg,
  onRightClick,
  isSidebar,
}: MessageInboundProps) {
  const {
    data: { data: user },
  } = useUser(message.uid);

  const hasProductLink = message.content.includes(
    `https://${
      process.env.NEXT_PUBLIC_IS_STAGING ? 'mxlocker.dev' : 'mxlocker.com'
    }/product/`
  );

  const productSlug = hasProductLink
    ? message.content.split('/')[message.content.split('/').length - 1]
    : '';

  return (
    <div
      className={cn(
        'flex items-end gap-[1.2rem] self-start py-[1.6rem] text-[1.6rem] font-medium',
        isSidebar ? 'sm:max-w-[40rem]' : 'max-w-[30rem] sm:max-w-[50rem]'
      )}
    >
      {isSupport ? (
        <div className="hidden h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-full bg-black p-3 sm:flex">
          <LogoWithoutText className="h-full w-full" fill="white" />
        </div>
      ) : (
        <Link
          className="hidden shrink-0 pb-[1.9rem] sm:block"
          href={`/profile/${user?.username_slug}`}
        >
          <Avatar size="small" user={user} circle />
        </Link>
      )}

      <div className="flex flex-col items-start gap-[1.6rem]">
        {!message.is_img ? (
          <div className="flex flex-col gap-[0.4rem]">
            <MessageTimestamp timestamp={timestamp} isOutbound={false} />

            {hasProductLink && <MessageProduct slug={productSlug} />}

            <MessageContent
              content={message.content}
              isOutbound={false}
              isSupport={isSupport}
              className="gap-[0.2rem]"
              isSidebar={isSidebar}
              onRightClick={onRightClick}
            />
          </div>
        ) : (
          <MessageImage
            message={message}
            img={message.thumbnail ?? message.content}
            fallbackImg={message.download_url ?? fallbackImg}
            onClick={onImageClick}
            isInbound
            onRightClick={onRightClick}
          />
        )}
      </div>
    </div>
  );
}
